// Anuncios de funcionalidad nueva — ADR-2026-08-31-anuncios-de-funcionalidad.
//
// El popup de "mirá esto nuevo". Un anuncio se define ACÁ, en el catálogo, y el
// resto de la app no sabe que existe: el modal se monta una sola vez en
// `ProtectedRoute` y pregunta a `anuncioVigente()` si hay algo para mostrar en la
// ruta actual.
//
// **Por qué el catálogo es código y no una tabla.** Un anuncio no es un dato: es una
// pieza de diseño (una cabecera visual propia, un copy escrito, un destino). Ponerlo
// en la base obligaría a guardar HTML o a inventar un mini lenguaje de layout, que es
// donde estas cosas se pudren. Lo que SÍ conviene mover a la base algún día es el
// interruptor —`activo`, `desde`, `hasta`— para poder apagar un anuncio sin
// desplegar; el resto vive bien acá.

/** Quién lo ve. Todo opcional: sin filtros, lo ve cualquier usuario logueado. */
export interface AudienciaAnuncio {
  /** Planes que lo ven. Sin esto, todos. */
  planes?: string[];
  /** `true` = sólo dev testers. Para probar un anuncio en producción antes de abrirlo. */
  soloDevTesters?: boolean;
}

export interface Anuncio {
  /**
   * Identifica al anuncio Y a su estado de "ya lo vi", que se guarda como
   * `anuncio:<id>` en `profiles.onboarding_seen`.
   *
   * **El id lleva fecha a propósito** (`referidos-2026-08`). Si mañana se quiere
   * volver a anunciar lo mismo —otra tanda, otro copy—, se cambia el id y el anuncio
   * vuelve a salir para todos. Reusar el id significaría "esto ya lo vieron", que es
   * justo lo contrario.
   */
  id: string;
  /** Interruptor principal. En `false` no se muestra a nadie, pase lo que pase. */
  activo: boolean;
  /** Ventana de vigencia, ISO. Opcionales: sin `desde` arranca ya, sin `hasta` no vence. */
  desde?: string;
  hasta?: string;
  /**
   * Dónde aparece. `'cualquiera'` = en la primera pantalla del shell que el usuario
   * abra. Una lista de rutas = sólo ahí.
   *
   * Anclarlo a una ruta no es un capricho de configuración: un anuncio que sale en
   * medio de otra tarea interrumpe; el mismo anuncio al entrar a la pantalla de la
   * que habla llega cuando el usuario ya está mirando el tema.
   */
  rutas: string[] | 'cualquiera';
  audiencia?: AudienciaAnuncio;
  /** Pastilla arriba del título ("Nuevo", "Beta"). Sin esto no se dibuja. */
  etiqueta?: string;
  titulo: string;
  /** Una oración. Si necesita dos, el anuncio está tratando de explicar de más. */
  cuerpo: string;
  /** Hasta 3 puntos con ícono. Opcional: sin esto, el anuncio es título + cuerpo. */
  puntos?: { icono: string; texto: string }[];
  /**
   * Botón principal. Sin `ruta` ni `accion`, sólo cierra.
   *
   * `accion` es para los anuncios que PIDEN algo en vez de llevar a algún lado. Hoy
   * sólo `'activar-push'`: el permiso de notificaciones únicamente se puede pedir
   * desde un click del usuario, así que tiene que colgar de este botón y no de una
   * navegación.
   */
  cta: { label: string; ruta?: string; accion?: 'activar-push' };
  /** Texto del botón secundario. Sin esto no se dibuja. */
  secundario?: string;
  /** Qué cabecera visual se dibuja. Cada valor es un componente en `AnuncioModal`. */
  visual: 'referidos' | 'push' | 'lanzamientos';
  /**
   * Condición de ESTE DISPOSITIVO, que el catálogo no puede evaluar solo: la calcula
   * quien dibuja el modal y la pasa en el contexto. Sin esto, el anuncio aplica en
   * cualquier dispositivo.
   *
   * `'push-pendiente'` = este navegador puede recibir push, todavía no lo activó, no
   * lo bloqueó, y el calendario de reintentos dice que toca preguntar.
   */
  condicion?: 'push-pendiente';
  /**
   * Qué pasa al cerrarlo. Default `'para-siempre'`: se guarda `anuncio:<id>` en
   * `profiles.onboarding_seen` y no vuelve nunca (es lo que hacen los anuncios de
   * funcionalidad — una novedad se cuenta una vez).
   *
   * `'reintentos'` = el cierre no se guarda como "visto" sino como un "ahora no" con
   * fecha, en el dispositivo (`lib/pushPopup`). Es para los anuncios que PIDEN algo:
   * a diferencia de una novedad, un pedido que la persona pospuso sigue teniendo
   * sentido más adelante.
   */
  cierre?: 'para-siempre' | 'reintentos';
  /**
   * Parámetro de la URL que lo suspende: quien llega con ese parámetro viene a hacer
   * algo puntual (y la pantalla ya le abrió su propio modal), así que un anuncio
   * encima sería un popup sobre otro. No se da por visto: sale en la próxima visita.
   */
  salvoConParametro?: string;
}

/**
 * El catálogo. Ordenado por prioridad: si dos anuncios aplican a la vez, gana el
 * primero — y el segundo sale la próxima. **Nunca se muestran dos juntos**: dos
 * popups encadenados es la forma más rápida de que el usuario aprenda a cerrar sin
 * leer.
 */
export const ANUNCIOS: Anuncio[] = [
  // Primero del catálogo: es el único que pierde valor si se pospone. Una novedad
  // contada tres pantallas más tarde es la misma novedad; el permiso de push se pide
  // una vez por escalón del calendario, y si otro anuncio se lo come, ese turno se
  // gastó sin haber preguntado nada.
  {
    id: 'activar-push-2026-09',
    activo: true,
    // En cualquier pantalla: el pedido tiene que encontrar al usuario. A diferencia
    // de un anuncio de funcionalidad, acá no hay una pantalla "de la que habla" a la
    // que valga la pena esperar que llegue.
    rutas: 'cualquiera',
    // Menos la llegada desde "Publicar en tu tienda" de Creaciones: abre el modal de
    // crear un lanzamiento y el pedido se le dibujaba encima (review Greptile #155).
    // Como el cierre es por reintentos, no se gasta el turno: sale en la próxima ruta.
    salvoConParametro: 'nuevo',
    condicion: 'push-pendiente',
    cierre: 'reintentos',
    // Sin pastilla: "Nuevo" anuncia una novedad del producto, y esto no anuncia nada
    // sino que pide algo. La etiqueta le agregaba una capa de lectura arriba del
    // título sin decir nada que el título no diga mejor.
    // El título promete el beneficio, no la función. "Activá las notificaciones"
    // describe el trabajo que le estamos pidiendo a la persona; esto describe lo que
    // se lleva. Es lo que hacen las referencias que se leen (Claude, VSCO, Peacock);
    // las que titulan "Turn on notifications" (X, Instagram) son las genéricas.
    titulo: 'Enterate cuando tus imágenes estén listas',
    cuerpo:
      'Te avisamos en este dispositivo cuando termina una generación o cuando hay algo para vos.',
    puntos: [
      { icono: 'photo_library', texto: 'Tus imágenes y videos, apenas están listos' },
      { icono: 'campaign', texto: 'Avisos del equipo de Atlas' },
      // El tercer punto es el que desarma la objeción real ("¿me van a llenar de
      // notificaciones?"). Claude y Venmo lo dicen igual, y por eso mismo: sin la
      // salida a la vista, el "Ahora no" es gratis y el "Activar" no.
      { icono: 'tune', texto: 'Lo apagás cuando quieras desde Configuración' },
    ],
    cta: { label: 'Activar notificaciones', accion: 'activar-push' },
    secundario: 'Ahora no',
    visual: 'push',
  },
  {
    id: 'lanzamientos-2026-09',
    activo: true,
    // Al entrar a la sección y no en cualquier pantalla: es una herramienta, no una
    // promo. Contarla cuando la persona ya abrió Lanzamientos es explicarle lo que
    // está mirando; contarla en la home es interrumpirla con algo que no buscó.
    // La ficha de un lanzamiento (`/lanzamientos/:id`) no entra: quien llega ahí ya
    // armó uno y no necesita que le presenten la sección.
    rutas: ['/lanzamientos'],
    // Desde "Publicar en tu tienda" de Creaciones se llega con el modal de crear
    // ya abierto: el anuncio esperaría a la próxima visita.
    salvoConParametro: 'nuevo',
    etiqueta: 'Nuevo',
    // El beneficio es el final del camino (la tienda llena), no la herramienta.
    titulo: 'Tus fotos, de Atlas a tu tienda en un solo paso',
    cuerpo:
      'Armás un lanzamiento con tus creaciones, completás cada producto y lo publicás todo junto.',
    puntos: [
      { icono: 'checklist', texto: 'Tildás lo que va y sacás lo que no' },
      { icono: 'auto_awesome', texto: 'La IA completa descripción, SEO y categorías' },
      { icono: 'storefront', texto: 'Publicás en Tienda Nube o lo bajás en un ZIP' },
    ],
    // Sin ruta: ya está en la pantalla, y el botón "Nuevo lanzamiento" queda a la
    // vista apenas se cierra.
    cta: { label: 'Armar mi lanzamiento' },
    secundario: 'Ahora no',
    visual: 'lanzamientos',
  },
  {
    id: 'referidos-2026-08',
    activo: true,
    // DEMO: atado a `/referidos` para poder verlo entrando a la pantalla.
    //
    // En producción esto va a ser `'cualquiera'` —el anuncio tiene que encontrar al
    // usuario, no esperar a que llegue solo a la pantalla que anuncia— o la home
    // (`/interfaz-v2`). Cambiar acá esa línea es todo lo que hace falta.
    rutas: ['/referidos'],
    etiqueta: 'Nuevo',
    titulo: 'Invitá a un amigo y ganan 50 créditos los dos',
    // La condición es la misma que dicen la pantalla de referidos, el alta y el
    // checkout: el premio se paga a los dos cuando el amigo ACTIVA SU PLAN
    // (ADR-2026-09-01-referidos-se-cobra-al-pagar). Este popup era la quinta
    // superficie y había quedado con la promesa vieja ("en cuanto empiece a usar").
    cuerpo:
      'Compartís tu link y, cuando tu amigo activa su plan, los créditos caen en las dos cuentas.',
    puntos: [
      { icono: 'link', texto: 'Compartís tu link por WhatsApp' },
      { icono: 'group_add', texto: 'Tu amigo se registra y activa su plan' },
      { icono: 'auto_awesome', texto: 'Los créditos caen solos en tu cuenta' },
    ],
    cta: { label: 'Ver mi link', ruta: '/referidos' },
    secundario: 'Ahora no',
    visual: 'referidos',
  },
];

/** Contexto del usuario que hace falta para decidir. */
export interface ContextoAnuncio {
  ruta: string;
  /** `location.search` de cuando se decide. */
  busqueda?: string;
  plan?: string | null;
  esDevTester?: boolean | null;
  /** `anuncio:<id>` de los que el usuario ya cerró. */
  yaVisto: (id: string) => boolean;
  /**
   * ¿Este dispositivo puede y debe recibir el pedido de push ahora? Lo calcula quien
   * dibuja el modal (estado del navegador + calendario de reintentos). Sin esto, los
   * anuncios con `condicion: 'push-pendiente'` no aplican.
   */
  pushPendiente?: boolean;
}

/** ¿Este anuncio aplica ahora, para este usuario, en esta ruta? */
function aplica(a: Anuncio, ctx: ContextoAnuncio, ahora: Date): boolean {
  if (!a.activo) return false;
  if (ctx.yaVisto(a.id)) return false;

  if (a.desde && ahora < new Date(a.desde)) return false;
  // `hasta` es el instante en que deja de mostrarse. Un anuncio vencido no vuelve
  // aunque el usuario nunca lo haya visto: es información que ya no es novedad.
  if (a.hasta && ahora > new Date(a.hasta)) return false;

  if (a.rutas !== 'cualquiera' && !a.rutas.includes(ctx.ruta)) return false;
  if (a.salvoConParametro && new URLSearchParams(ctx.busqueda ?? '').has(a.salvoConParametro)) {
    return false;
  }

  // La condición del dispositivo se chequea en positivo: mientras el contexto no
  // afirme que corresponde, no se muestra. Si el modal todavía no terminó de mirar el
  // navegador, `pushPendiente` llega `undefined` y el anuncio espera — preguntar por
  // un permiso que quizá ya esté dado es peor que preguntar un segundo más tarde.
  if (a.condicion === 'push-pendiente' && ctx.pushPendiente !== true) return false;

  const aud = a.audiencia;
  if (aud?.soloDevTesters && ctx.esDevTester !== true) return false;
  if (aud?.planes && aud.planes.length > 0) {
    // Sin plan conocido todavía, NO se muestra: mostrarle a alguien un anuncio de
    // una audiencia a la que capaz no pertenece es peor que mostrarlo un rato más
    // tarde, cuando el plan ya cargó.
    if (!ctx.plan || !aud.planes.includes(ctx.plan)) return false;
  }
  return true;
}

// ── Coordinación con los recorridos guiados ─────────────────────────────────
//
// El anuncio tarda en decidir (lee la sesión, el "ya lo vi" y el estado de push), y
// los recorridos de una pantalla (`pages/lanzamientos/recorridos.ts`) arrancan solos al
// entrar. Mirando sólo el DOM, el recorrido llegaba primero y el anuncio se le dibujaba
// encima. Por eso el modal anuncia en qué está, y el recorrido espera a que quede
// `libre`: primero el popup del centro, después los cartelitos.

export type EstadoDelAnuncio = 'decidiendo' | 'mostrando' | 'libre';

/** Arranca en `decidiendo`: hasta que el modal mire la primera ruta, no se sabe. */
let estadoDelAnuncio: EstadoDelAnuncio = 'decidiendo';

/** Lo llama `AnuncioModal` en cada paso: al cambiar de ruta, al mostrar y al terminar. */
export function marcarEstadoDelAnuncio(e: EstadoDelAnuncio): void {
  estadoDelAnuncio = e;
}

/** ¿Hay un anuncio en pantalla o por decidirse? Mientras sea así, nada se le dibuja encima. */
export function anuncioPendiente(): boolean {
  return estadoDelAnuncio !== 'libre';
}

/** El primero del catálogo que aplique, o `null`. */
export function anuncioVigente(
  ctx: ContextoAnuncio,
  ahora: Date = new Date(),
): Anuncio | null {
  return ANUNCIOS.find((a) => aplica(a, ctx, ahora)) ?? null;
}
